import { createContext, useContext, useState, ReactNode } from 'react'
import { clsx } from 'clsx'
import { useTranslation } from '../../core/hooks/useTranslation'

interface TabsContextType {
  activeTab: string
  setActiveTab: (tab: string) => void
}

const TabsContext = createContext<TabsContextType | undefined>(undefined)

interface TabsProps {
  children: ReactNode
  defaultTab?: string
  onTabChange?: (tab: string) => void
}

export function Tabs({ children, defaultTab, onTabChange }: TabsProps) {
  const [activeTab, setActiveTab] = useState(defaultTab || '')

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    onTabChange?.(tab)
  }

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab: handleTabChange }}>
      <div className="w-full">{children}</div>
    </TabsContext.Provider>
  )
}

interface TabsListProps {
  children: ReactNode
  className?: string
  variant?: 'horizontal' | 'vertical'
}

export function TabsList({ children, className, variant = 'horizontal' }: TabsListProps) {
  const { isRTL } = useTranslation()

  if (variant === 'vertical') {
    return (
      <div className={clsx(
        'flex flex-col space-y-1 mb-6',
        isRTL ? 'md:ml-6 md:border-l md:pl-4' : 'md:mr-6 md:border-r md:pr-4',
        className
      )}>
        {children}
      </div>
    )
  }

  return (
    <div className={clsx(
      'flex space-x-1 border-b border-gray-200 mb-6 overflow-x-auto scrollbar-hide',
      isRTL && 'space-x-reverse',
      className
    )}>
      {children}
    </div>
  )
}

interface TabsTriggerProps {
  value: string
  children: ReactNode
  icon?: ReactNode
  className?: string
}

export function TabsTrigger({ value, children, icon, className }: TabsTriggerProps) {
  const context = useContext(TabsContext)
  const { isRTL } = useTranslation()

  if (!context) {
    throw new Error('TabsTrigger must be used within Tabs component')
  }

  const { activeTab, setActiveTab } = context
  const isActive = activeTab === value

  return (
    <button
      onClick={() => setActiveTab(value)}
      className={clsx(
        'flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all duration-200 rounded-t-lg whitespace-nowrap relative',
        isActive
          ? 'text-primary-600 border-b-2 border-primary-500 bg-primary-50/50 font-semibold'
          : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50/30 hover:border-b-2 hover:border-primary-200',
        className
      )}
      aria-selected={isActive}
      role="tab"
    >
      {icon && (
        <span className={clsx(
          'w-4 h-4 flex-shrink-0 transition-colors',
          isActive ? 'text-primary-600' : 'text-gray-500'
        )}>
          {icon}
        </span>
      )}
      <span>{children}</span>
      {isActive && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 rounded-full"></div>
      )}
    </button>
  )
}

interface TabsContentProps {
  value: string
  children: ReactNode
  className?: string
}

export function TabsContent({ value, children, className }: TabsContentProps) {
  const context = useContext(TabsContext)

  if (!context) {
    throw new Error('TabsContent must be used within Tabs component')
  }

  const { activeTab } = context

  if (activeTab !== value) {
    return null
  }

  return (
    <div className={clsx('animate-fade-in', className)}>
      {children}
    </div>
  )
}

